import { Box, Button, ButtonProps, Flex } from "@chakra-ui/react";
import { Global } from "@emotion/react";
import { PropsWithChildren, createContext, useContext, useRef, useState } from "react";
import { IoIosResize } from "react-icons/io";

import { ScreenshotProps, useScreenshot } from "./use-screenshot";
import { A4_LONG_SIDE, A4_SHORT_SIDE } from "./utils/pdf-consts";
interface InitialOptions {
  screenshotOptions: Omit<ScreenshotProps, "screenshotWrapperRef">;
  sizes: {
    ratio?: [number, number];
    width?: number;
    height?: number;
    left?: number;
    top?: number;
  };
}

export const HIDE_PRINT_CLASS = "hide-print";
const HIDE_PRINT_CLASS_SX = { [`.${HIDE_PRINT_CLASS}`]: { display: "none !important" } };

type ShowscreenShotOverlayArgs = ScreenshotProps;

const ScreenshotContext = createContext<{
  showScreenshotOverlay: (opts: ShowscreenShotOverlayArgs) => void;
} | null>(null);

export const useFramedScreenshotOverlay = (args: ShowscreenShotOverlayArgs) => {
  const context = useContext(ScreenshotContext);
  if (!context)
    throw new Error("useFramedScreenshotOverlay must be used within FramedScreenshotProvider");
  return () => context.showScreenshotOverlay(args);
};

type FramedScreenshotProviderProps = {
  cancelButtonProps: ButtonProps;
  confirmButtonProps: ButtonProps;
};

export const FramedScreenshotProvider = ({
  children,
  ...rest
}: PropsWithChildren<FramedScreenshotProviderProps>) => {
  const [initialOptions, setInitialOptions] = useState<InitialOptions | undefined>(undefined);

  return (
    <ScreenshotContext.Provider
      value={{
        showScreenshotOverlay: ({
          screenshotWrapperRef,
          pdf,
          orientation = "landscape",
          ...rest
        }) => {
          const ratio: [number, number] | undefined = pdf
            ? orientation === "landscape"
              ? [A4_LONG_SIDE, A4_SHORT_SIDE]
              : [A4_SHORT_SIDE, A4_LONG_SIDE]
            : undefined;
          const screenshotOptions = { ...rest, pdf, orientation };

          const element = screenshotWrapperRef.current;

          if (element) {
            const { width, height, left, top } = element.getBoundingClientRect();
            return setInitialOptions({
              sizes: { ratio, width, height, left, top },
              screenshotOptions,
            });
          }

          setInitialOptions({ sizes: { ratio }, screenshotOptions });
        },
      }}
    >
      {children}

      <Global
        styles={{
          "@media print": HIDE_PRINT_CLASS_SX,
          ...(initialOptions ? HIDE_PRINT_CLASS_SX : {}),
        }}
      />

      {initialOptions && (
        <FramedScreenshotInternal
          {...rest}
          {...initialOptions}
          cleanup={() => setInitialOptions(undefined)}
        />
      )}
    </ScreenshotContext.Provider>
  );
};

// This gets the initial box. If there is a ratio provided use the other sizes as a bounding box for the ratio and place it in the middle as much as will fit
function getBoxDimensions({
  ratio,
  width = window.innerWidth,
  height = window.innerHeight,
  left = 0,
  top = 0,
}: {
  ratio?: [number, number];
  width?: number;
  height?: number;
  left?: number;
  top?: number;
}) {
  let boxWidth: number;
  let boxHeight: number;

  if (ratio) {
    const [wRatio, hRatio] = ratio;

    // Try to fill as much of the bounding box as possible while preserving aspect ratio
    const maxHeightForWidth = (width * hRatio) / wRatio;
    const maxWidthForHeight = (height * wRatio) / hRatio;

    if (maxHeightForWidth <= height) {
      boxWidth = width;
      boxHeight = maxHeightForWidth;
    } else {
      boxHeight = height;
      boxWidth = maxWidthForHeight;
    }
  } else {
    boxWidth = width;
    boxHeight = height;
  }

  const centeredLeft = left + (width - boxWidth) / 2;
  const centeredTop = top + (height - boxHeight) / 2;

  return {
    ratio,
    width: boxWidth,
    height: boxHeight,
    left: centeredLeft,
    top: centeredTop,
  };
}

const FramedScreenshotInternal = ({
  cleanup,
  screenshotOptions,
  sizes,
  cancelButtonProps,
  confirmButtonProps,
}: {
  cleanup: () => void;
} & InitialOptions &
  FramedScreenshotProviderProps) => {
  const screenshotWrapperRef = useRef<HTMLDivElement>(null);
  const box = getBoxDimensions(sizes);
  const { ratio, width, height } = box;
  let { left, top } = box;

  const [screenshotting, setScreenshotting] = useState(false);

  const screenshot = useScreenshot({
    screenshotWrapperRef,
    ...screenshotOptions,
  });

  let offsetX = 0;
  let offsetY = 0;
  let isDragging = false;
  let isResizing = false;

  const handleMouseDown = (e: MouseEvent) => {
    const box = screenshotWrapperRef.current;
    if (!box) return;

    if ((e.target as HTMLElement).classList.contains("resize-handle")) {
      isResizing = true;
    } else {
      isDragging = true;
      offsetX = e.clientX - left;
      offsetY = e.clientY - top;
    }

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    e.preventDefault();
  };

  const handleMouseMove = (e: MouseEvent) => {
    const box = screenshotWrapperRef.current;
    if (!box) return;

    if (isDragging) {
      left = e.clientX - offsetX;
      top = e.clientY - offsetY;

      left = Math.max(0, Math.min(left, window.innerWidth - box.offsetWidth));
      top = Math.max(0, Math.min(top, window.innerHeight - box.offsetHeight));

      box.style.left = `${left}px`;
      box.style.top = `${top}px`;
    } else if (isResizing) {
      const boxRect = box.getBoundingClientRect();
      const newWidth = e.clientX - boxRect.left;

      if (ratio) {
        const newHeight = (newWidth * ratio[1]) / ratio[0];
        box.style.width = `${newWidth}px`;
        box.style.height = `${newHeight}px`;
      } else {
        const newHeight = e.clientY - boxRect.top;
        box.style.width = `${newWidth}px`;
        box.style.height = `${newHeight}px`;
      }
    }
  };

  const handleMouseUp = () => {
    isDragging = false;
    isResizing = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  return (
    <>
      <Flex
        opacity={screenshotting ? 0 : 1}
        position="fixed"
        bottom={4}
        right={4}
        zIndex={99991}
        gap={2}
      >
        <Button {...cancelButtonProps} onClick={cleanup}>
          Cancel
        </Button>
        <Button
          {...confirmButtonProps}
          onClick={async (e) => {
            setScreenshotting(true);
            await screenshot(e);
            setScreenshotting(false);
            cleanup();
          }}
        >
          Screenshot
        </Button>
      </Flex>

      <Box
        ref={screenshotWrapperRef}
        opacity={screenshotting ? 0 : 1}
        position="fixed"
        left={`${left}px`}
        top={`${top}px`}
        width={`${width}px`}
        height={`${height}px`}
        background="white"
        border="2px dashed black"
        boxShadow="0 0 0 9999px rgba(0,0,0,0.4)"
        zIndex={9999}
        p={4}
        bg="transparent"
        boxSizing="border-box"
        cursor="move"
        style={{ resize: "none", overflow: "hidden", userSelect: "none" }}
        onMouseDown={(e) => {
          if (
            (e.target as HTMLElement) === screenshotWrapperRef.current ||
            (e.target as HTMLElement).classList.contains("resize-handle")
          ) {
            handleMouseDown(e.nativeEvent);
          }
        }}
        _before={{
          content: '""',
          position: "absolute",
          inset: 0,
          border: "1px solid white",
          pointerEvents: "none",
        }}
      >
        <Box
          className="resize-handle"
          position="absolute"
          bottom="2px"
          right="2px"
          cursor="nwse-resize"
          userSelect="none"
          onMouseDown={(e) => handleMouseDown(e.nativeEvent)}
          border="black 1px solid"
          borderRadius="sm"
          p="1px"
        >
          <IoIosResize color="black" pointerEvents="none" transform="scale(-1, 1)" />
        </Box>
      </Box>
    </>
  );
};
